import type { OrderingComponents } from "@steelyard/api-client";
import { ordering as orderingClient, unwrap } from "../api";
import { parseIsoDate } from "../format";
import { isGuid, orderPath, redirectToCanonical } from "../paths";

/**
 * Ported from apps/admin/lib/api.ts's `ordering()`/`orderingWritable()` clients (routed at
 * `/api/orders`, not `/api/ordering` — the doc name and gateway prefix differ for this one
 * service) plus apps/admin/app/(app)/orders/{orders.ts,actions.ts}. Nothing here calculates —
 * `money()` only narrows the wire's `number | string` to `number | null`, never rounds or sums.
 */

export const ORDER_STATES = [
  { state: "placed", label: "Placed" },
  { state: "awaiting_payment", label: "Awaiting payment" },
  { state: "awaiting_company_approval", label: "Awaiting company approval" },
  { state: "awaiting_approval", label: "Awaiting approval" },
  { state: "approved", label: "Approved" },
  { state: "picking_packing", label: "Picking / Packing" },
  { state: "packed", label: "Packed" },
  { state: "invoiced", label: "Invoiced" },
  { state: "dispatched", label: "Dispatched" },
  { state: "delivered", label: "Delivered" },
  { state: "cancelled", label: "Cancelled" },
] as const;

/**
 * `invoiced` is deliberately absent — only Billing's inbound post may make that transition. So are
 * `dispatched` and `delivered`: they go through the dispatcher workflow (assign a dispatcher and a
 * delivery type, then dispatch/deliver or the third-party handover), and the advance endpoint
 * refuses them with 409 `dispatch_workflow_required`. And `packed`: only the warehouse completing its
 * packing task makes an order packed — that records the packed quantities the invoice is raised on —
 * and the advance endpoint refuses it with 409 `packing_completion_required`.
 */
export const ADVANCE_TARGETS = ["picking_packing"] as const;

/**
 * The one `ADVANCE_TARGETS` value reachable from each state, mirroring the backend's
 * `OrderStateMachine.Permitted` table (`Steelyard.Ordering.Domain/Orders/OrderStateMachine.cs`) —
 * not a calculation, a fixed vocabulary already duplicated once in `ADVANCE_TARGETS` itself. A
 * state absent here (e.g. `picking_packing` — only the warehouse's completed pack moves it on;
 * `packed` — only Billing's invoice; `invoiced` — the dispatch workflow) offers no move at all. Every entry has at most one target: this subset of
 * the state machine is a straight line, so "the next step" is never ambiguous. Without this, the
 * "Move to" menu offered every remaining target regardless of the order's actual state, and picking
 * any target more than one step ahead always 409'd.
 */
const NEXT_ADVANCE_TARGET: Partial<Record<string, (typeof ADVANCE_TARGETS)[number]>> = {
  approved: "picking_packing",
};

export function nextAdvanceTargets(state: string): readonly (typeof ADVANCE_TARGETS)[number][] {
  const next = NEXT_ADVANCE_TARGET[state];
  return next ? [next] : [];
}

const PICKING = ORDER_STATES.findIndex((row) => row.state === "picking_packing");

/**
 * Also labels the legacy `picking` and `packing` states, which the backend merged into
 * `picking_packing` but which old history rows still carry — the fallback below already renders
 * them as "Picking" / "Packing", so they are deliberately not in `ORDER_STATES` (menus, filters).
 */
export function stateLabel(state: string): string {
  const known = ORDER_STATES.find((row) => row.state === state)?.label;
  if (known) return known;
  const words = state.replaceAll("_", " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const PACKED = ORDER_STATES.findIndex((row) => row.state === "packed");

/**
 * Whether the order is still before `packed` — the last moment Ordering accepts a trade discount
 * (D-39), because Billing raises the invoice from the packed order. A display rule mirroring the
 * API's own refusal (`discount_too_late`), which stays the authority; an unknown state counts as
 * past it.
 */
export function beforePacked(state: string): boolean {
  const index = ORDER_STATES.findIndex((row) => row.state === state);
  return index >= 0 && index < PACKED;
}

/** Whether Warehouse's completed pack reached the order — only it records an actual quantity. */
export function packRecorded(order: Pick<OrderDetail, "lines">): boolean {
  return order.lines.some((line) => line.actualQuantity !== null || line.actualPieceCount !== null);
}

const APPROVED = ORDER_STATES.findIndex((row) => row.state === "approved");

const INVOICED = ORDER_STATES.findIndex((row) => row.state === "invoiced");

/** What an order already holds, as far as a forced move past Packed is concerned. */
export type OrderFacts = { packRecorded: boolean; invoiced: boolean };

/** Whether `state` is at or past `step` in the lifecycle. */
export function reached(state: string, step: string): boolean {
  return ORDER_STATES.findIndex((row) => row.state === state) >= ORDER_STATES.findIndex((row) => row.state === step);
}

/**
 * The lifecycle states an order still has to pass through, in order, before it can be `target` —
 * empty when a forced move there is open. A forward move to Packed, Invoiced, Dispatched or
 * Delivered is never forced: each is a step with its own record (the pack, the invoice, the
 * dispatch, the delivery), so the steps are taken instead (`pages/orders/Complete.tsx`). A step the
 * order was forced past without its record — a pack never recorded, an invoice never finalised — is
 * listed again. Mirrors the override endpoint's refusals (`packing_completion_required`,
 * `invoice_required`), which stay the authority. Any move back stays open.
 */
export function stepsBefore(state: string, facts: OrderFacts, target: string): string[] {
  if (!["invoiced", "dispatched", "delivered"].includes(target) && !(target === "packed" && beforePacked(state))) return [];
  const at = ORDER_STATES.findIndex((row) => row.state === state);
  const to = ORDER_STATES.findIndex((row) => row.state === target);
  const from =
    !facts.packRecorded && at >= PACKED ? PACKED : !facts.invoiced && at >= INVOICED ? INVOICED : at < APPROVED ? APPROVED : at + 1;
  return ORDER_STATES.slice(from, to + 1).map((row) => row.state);
}

/** An unrecognised state counts as "begun" — the safe guess when its lifecycle position is unknown. */
export function pickingBegun(state: string): boolean {
  const index = ORDER_STATES.findIndex((row) => row.state === state);
  return index < 0 || index >= PICKING;
}

/** Parses the wire's `number | string`. Not a calculation: no rounding, no scaling. */
export function money(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === "") return null;
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** The API's own shapes, generated from `openapi/ordering.json` — never a hand-written second copy. */
type Schemas = OrderingComponents["schemas"];
export type OrderSummary = Schemas["OrderSummaryView"];
export type OrderLine = Schemas["OrderLineView"];
export type OrderHistoryEntry = Schemas["OrderTransitionView"];
export type OrderDetail = Schemas["OrderDetailView"];

export type PagedResult<T> = { items: T[]; page: number; pageSize: number; totalCount: number; totalPages?: number };

export type DeliveryZone = { pincodeFrom: string; pincodeTo: string; charge: number | string };
export type DeliveryZonesResult = { zones: DeliveryZone[]; unmatchedPincodes: string[] };

export async function listOrders(params: {
  page: number;
  pageSize?: number;
  state?: string;
  orderNumber?: string;
  customerId?: string;
  from?: string;
  to?: string;
}) {
  return unwrap(
    (await orderingClient()).GET("/api/orders/admin/orders", {
      params: {
        query: {
          page: params.page,
          pageSize: params.pageSize ?? 50,
          state: params.state,
          orderNumber: params.orderNumber,
          customerId: params.customerId,
          ...placedRange(params.from, params.to),
        },
      },
    }),
  );
}

/**
 * The picker's calendar days as instants: `from` from the start of its day, `to` through the end of
 * its day, both in the browser's own timezone — the one the Placed column is shown in. A bare
 * `2026-10-14` binds server-side as that day's midnight on the server's clock, which made `to`
 * exclude its own day (13 Oct → 13 Oct matched nothing).
 */
export function placedRange(from?: string, to?: string): { from?: string; to?: string } {
  const start = parseIsoDate(from);
  const end = parseIsoDate(to);
  end?.setHours(23, 59, 59, 999);
  return { from: start?.toISOString(), to: end?.toISOString() };
}

/**
 * 008-reporting-dashboards T009 — the dashboard's orders-by-status tile, and (later)
 * `/reports/pipeline`. A flat `{state: count}` map, counts typed `number | string` on the wire same
 * as every other decimal here. Verified live against the running gateway 2026-09-09: keys included
 * every state in `ORDER_STATES` **plus `cart`**, which isn't one of them — `stateLabel()` already
 * falls back to a humanised version of any state it doesn't recognise, so that's rendered as-is
 * rather than dropped.
 */
export async function getOrderPipeline() {
  return unwrap(
    (await orderingClient()).GET("/api/orders/admin/orders/pipeline"),
  );
}

/**
 * 008-reporting-dashboards T018/T020 (FR-013, SC-005). `difference` and `attribution` are the API's
 * own figures, never recomputed from `orderedValue`/`invoicedValue` here (constitution I) — T018's
 * test greps this file's caller for exactly that.
 *
 * `OrderToInvoiceRow` carries no `invoiceId` (verified against the generated type and the backend's
 * own sample payload in `008-reporting/contracts/reports.md`). The only invoice-by-order lookup is
 * `GET /api/billing/internal/orders/{orderId}/invoice` — an internal, unauthenticated
 * service-to-service route, not an admin-facing operation — and no packing-entries-by-order screen
 * exists in this app yet (005/007's admin oversight surface for packing isn't built). Each row links
 * to `/orders/{orderId}` instead — the existing order detail screen, which already surfaces
 * `invoiceNumber`/`invoicedTotal` — a partial reading of FR-013's "link to the invoice and the
 * packing entries", named rather than invented (see this feature's outcome notes).
 */
export type OrderToInvoiceRow = {
  orderId: string;
  orderNumber: string;
  customerId: string;
  orderedValue: number | string;
  invoicedValue: number | string;
  freightExcluded: number | string;
  difference: number | string;
  attribution: string;
  invoicedAt: string;
};

/** Nets positive against negative on purpose (FR-009/DA-03, backend contract) — never recomputed here. */
export type OrderToInvoiceAggregate = {
  orderCount: number | string;
  orderedValue: number | string;
  invoicedValue: number | string;
  net: number | string;
  /** `null` over an empty denominator, never coerced to 0 (FR-027/DA-10, same rule as `varianceRate`). */
  percentOfInvoiced: number | string | null;
};

export type OrderToInvoiceReport = {
  periodFrom: string;
  periodTo: string;
  dateField: string;
  sources: string[];
  empty: boolean;
  emptyReason: string | null;
  producedAt: string;
  rows: OrderToInvoiceRow[];
  aggregate: OrderToInvoiceAggregate;
  /** Always stated, never a silently partial aggregate (FR-012, DA-04 on the backend contract). */
  excludedUninvoicedOrders: number | string;
  excludedB2cOrders: number | string;
};

/** One of `weight_variance` | `short_fulfilment` (FR-011/DA-05) — titled for display, the wire value is what's compared. */
export function attributionLabel(attribution: string): string {
  if (attribution === "weight_variance") return "Weight variance";
  if (attribution === "short_fulfilment") return "Short fulfilment";
  return attribution.replaceAll("_", " ");
}

/**
 * The dashboard's orders-placed trend — `GET /api/orders/reports/order-trend` (backend
 * ordering-admin.md §12). Zero-filled buckets and `totals` both come from the API; nothing here
 * sums them.
 */
export type OrderTrendReport = OrderingComponents["schemas"]["OrderTrendReport"];

export async function getOrderTrend(params: { from: string; to: string; bucket: "day" | "month" }): Promise<OrderTrendReport> {
  return unwrap(
    (await orderingClient()).GET("/api/orders/reports/order-trend", { params: { query: params } }),
  );
}

export async function getOrderToInvoiceReport(params: { from: string; to: string }) {
  return unwrap(
    (await orderingClient()).GET("/api/orders/reports/order-to-invoice", { params: { query: params } }),
  ) as Promise<OrderToInvoiceReport>;
}

export async function getOrder(id: string) {
  return unwrap(
    (await orderingClient()).GET("/api/orders/admin/orders/{orderId}", { params: { path: { orderId: id } } }),
  );
}

export async function getOrderByNumber(orderNumber: string) {
  return unwrap(
    (await orderingClient()).GET("/api/orders/admin/orders/by-number/{orderNumber}", { params: { path: { orderNumber } } }),
  );
}

/**
 * The hash of an In-House delivery's signature image, kept in Files (016), or `null` when none was
 * recorded. Only the supervisor's dispatch view carries it, never the order itself.
 */
export async function deliverySignatureHash(orderId: string): Promise<string | null> {
  const view = await unwrap(
    (await orderingClient()).GET("/api/orders/admin/orders/{orderId}/dispatch", { params: { path: { orderId } } }),
  );
  // TODO(016 T080): remove cast after openapi regen (the snapshot still names it `signature`).
  return (view.delivery as { signatureHash?: string | null } | null)?.signatureHash ?? null;
}

/** The order's dispatch record: delivery type, and per line the quantity Billing invoiced. */
export async function getDispatchView(orderId: string) {
  return unwrap(
    (await orderingClient()).GET("/api/orders/admin/orders/{orderId}/dispatch", { params: { path: { orderId } } }),
  );
}

export type DeliveryType = "in_house" | "third_party";

export async function setDeliveryType(orderId: string, deliveryType: DeliveryType) {
  return unwrap(
    (await orderingClient()).PUT("/api/orders/admin/orders/{orderId}/delivery-type", {
      params: { path: { orderId } },
      body: { deliveryType, reason: null },
    }),
  );
}

/** In-House only: who takes it out and records the delivery. Assigning the one already assigned changes nothing. */
export async function assignDispatcher(orderId: string, dispatcherId: string) {
  return unwrap(
    (await orderingClient()).PUT("/api/orders/admin/orders/{orderId}/dispatcher", {
      params: { path: { orderId } },
      body: { dispatcherId },
    }),
  );
}

/** In-House `invoiced → dispatched`. A Super Admin needs no dispatcher assigned. */
export async function dispatchInHouse(orderId: string) {
  return unwrap(
    (await orderingClient()).POST("/api/orders/dispatch/orders/{orderId}/dispatch", { params: { path: { orderId } } }),
  );
}

/**
 * In-House `dispatched → delivered`, one line per invoiced line, checked against Billing's
 * quantities. `signature` is the image as a data URI; Ordering stores it in Files.
 */
export async function deliverInHouse(
  orderId: string,
  delivery: { receiverName: string; signature: string; lines: { orderLineId: string; deliveredQuantity: number; shortageReason: string | null }[] },
) {
  return unwrap(
    (await orderingClient()).POST("/api/orders/dispatch/orders/{orderId}/deliver", {
      params: { path: { orderId } },
      body: { ...delivery, completedAt: null },
    }),
  );
}

/** Third-Party `invoiced → dispatched`. No transport record is needed. */
export async function handOverToThirdParty(orderId: string) {
  return unwrap(
    (await orderingClient()).POST("/api/orders/admin/orders/{orderId}/third-party-handover", { params: { path: { orderId } } }),
  );
}

export async function confirmThirdPartyDelivered(orderId: string) {
  return unwrap(
    (await orderingClient()).POST("/api/orders/admin/orders/{orderId}/third-party-delivered", { params: { path: { orderId } } }),
  );
}

/** The order a route's `:orderNumber` names. A legacy id throws a redirect to the number URL, query kept. */
export async function orderByKey(request: Request, key: string) {
  if (!isGuid(key)) return getOrderByNumber(key);
  throw redirectToCanonical(request, key, orderPath((await getOrder(key)).orderNumber));
}

export async function approveOrder(id: string) {
  return unwrap(
    (await orderingClient()).POST("/api/orders/admin/orders/{orderId}/approve", { params: { path: { orderId: id } } }),
  );
}

export async function rejectOrder(id: string, reason: string) {
  return unwrap(
    (await orderingClient()).POST("/api/orders/admin/orders/{orderId}/reject", {
      params: { path: { orderId: id } },
      body: { reason },
    }),
  );
}

export async function cancelOrder(id: string, reason: string) {
  return unwrap(
    (await orderingClient()).POST("/api/orders/admin/orders/{orderId}/cancel", {
      params: { path: { orderId: id } },
      body: { reason },
    }),
  );
}

export async function advanceOrder(id: string, state: string) {
  return unwrap(
    (await orderingClient()).POST("/api/orders/admin/orders/{orderId}/advance", {
      params: { path: { orderId: id } },
      body: { state },
    }),
  );
}

/** Super Admin only (`orders.override`): any state, outside the lifecycle table, with a reason. */
export async function overrideOrderState(id: string, state: string, reason: string) {
  return unwrap(
    (await orderingClient()).POST("/api/orders/admin/orders/{orderId}/override-state", {
      params: { path: { orderId: id } },
      body: { state, reason },
    }),
  );
}

/** Spec §11.1's lifecycle (payment is the B2C step) plus §11.3's cancellation — mirrors `Order.OverrideTargets`. */
const OVERRIDE_STATES = ["placed", "awaiting_payment", "approved", "picking_packing", "packed", "invoiced", "dispatched", "delivered", "cancelled"];

/** The states a Super Admin may set from `current`. */
export function overrideTargets(current: string): string[] {
  // Cancelled is final: its stock was released, so there is nothing to move it on to.
  if (current === "cancelled") return [];
  return OVERRIDE_STATES.filter((state) => state !== current);
}

export async function amendLines(id: string, lines: { orderLineId: string; quantity: number }[]) {
  return unwrap(
    (await orderingClient()).PATCH("/api/orders/admin/orders/{orderId}/lines", {
      params: { path: { orderId: id } },
      body: { lines },
    }),
  );
}

/**
 * D-39: grants, changes or removes (0) a B2B order's trade discount. Super Admin (`orders.discount`).
 * The API re-prices the order — the discount, the GST on the discounted value and the total are all
 * its figures, never this screen's.
 */
/** FR-052: refused with 409 once picking/packing has started. Delivery is re-priced for the new pincode. */
export async function changeShippingAddress(id: string, shippingAddressId: string, pincode: string) {
  return unwrap(
    (await orderingClient()).POST("/api/orders/admin/orders/{orderId}/shipping-address", {
      params: { path: { orderId: id } },
      body: { shippingAddressId, pincode },
    }),
  );
}

export async function setOrderDiscount(id: string, discountPercent: number, reason: string) {
  return unwrap(
    (await orderingClient()).PUT("/api/orders/admin/orders/{orderId}/discount", {
      params: { path: { orderId: id } },
      body: { discountPercent, reason },
    }),
  );
}

export async function decideShortFulfilment(id: string, decision: "ship_short" | "hold", reason: string) {
  return unwrap(
    (await orderingClient()).POST("/api/orders/admin/orders/{orderId}/short-fulfilment", {
      params: { path: { orderId: id } },
      body: { decision, reason },
    }),
  );
}

export async function getDeliveryZones() {
  return unwrap((await orderingClient()).GET("/api/orders/admin/delivery-zones")) as Promise<DeliveryZonesResult>;
}

export async function replaceDeliveryZones(zones: { pincodeFrom: string; pincodeTo: string; charge: number }[]) {
  return unwrap((await orderingClient()).PUT("/api/orders/admin/delivery-zones", { body: { zones } }));
}

/** FR-048's three modes, as the API spells them. */
export type DeliveryChargeMode = "flat" | "pincode_zone" | "free_above_value";
export type DeliverySettings = Schemas["DeliverySettingsView"];
export type DeliverySettingsInput = Schemas["SetDeliverySettingsRequest"];

/** The delivery-charge rule in force — saved here, or configuration's (`source`) until someone saves. */
export async function getDeliverySettings() {
  return unwrap((await orderingClient()).GET("/api/orders/admin/delivery-settings"));
}

/** Saves the rule; answers the rule now in force. 422 names what a mode is missing. */
export async function setDeliverySettings(body: DeliverySettingsInput) {
  return unwrap((await orderingClient()).PUT("/api/orders/admin/delivery-settings", { body }));
}

/** FR-056: what happens when an order is packed short, as the API spells the automatic decision. */
export type ShortFulfilmentChoice = "ship_short" | "hold";
export type FulfilmentSettings = Schemas["FulfilmentSettingsView"];
export type FulfilmentSettingsInput = Schemas["SetFulfilmentSettingsRequest"];

/** Whether a short order waits for an administrator — saved here, or configuration's (`source`) until someone saves. */
export async function getFulfilmentSettings() {
  return unwrap((await orderingClient()).GET("/api/orders/admin/fulfilment-settings"));
}

/** Saves the choice; answers the behaviour now in force. Applies to the next order packed short. */
export async function setFulfilmentSettings(body: FulfilmentSettingsInput) {
  return unwrap((await orderingClient()).PUT("/api/orders/admin/fulfilment-settings", { body }));
}
